import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, User, ArrowRight, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import CTABanner from '../components/CTABanner';
import Footer from '../components/Footer';
import { useStructuredData } from '../hooks/useStructuredData';

const POSTS_PER_PAGE = 9;

const BlogListing = () => {
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPosts, setFilteredPosts] = useState([]);

  // Helper to construct full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    
    // Handle if url is an object (thumbnail object)
    if (typeof url === 'object' && url.url) {
      url = url.url;
    }
    
    // SIMPLE: Just return the URL as-is
    // Upload endpoint now returns full URLs
    return url;
  };

  // Helper to get blog post URL with category structure
  const getPostUrl = (post) => {
    if (post.primary_category && categories.length > 0) {
      const category = categories.find(c => c.id === post.primary_category);
      if (category) {
        return `/blog/${category.slug}/${post.slug}`;
      }
    }
    return `/blog/${post.slug}`;
  };

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  // Set selected category from URL parameter
  useEffect(() => {
    if (categorySlug && categories.length > 0) {
      const category = categories.find(c => c.slug === categorySlug);
      if (category) {
        setSelectedCategory(category.id);
      }
    } else if (!categorySlug) {
      setSelectedCategory(null);
    }
  }, [categorySlug, categories]);

  useEffect(() => {
    filterPosts();
  }, [posts, selectedCategory, searchQuery]);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/blog/posts', { params: { limit: 200 } });
      setPosts(response.data.posts || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/blog/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const filterPosts = () => {
    let filtered = [...posts];

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(post => 
        post.categories && post.categories.includes(selectedCategory)
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(query) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(query)) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    setFilteredPosts(filtered);
  };

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedPosts = filteredPosts.slice((safePage - 1) * POSTS_PER_PAGE, safePage * POSTS_PER_PAGE);

  const goToPage = (page) => {
    const params = new URLSearchParams(searchParams);
    if (page <= 1) {
      params.delete('page');
    } else {
      params.set('page', String(page));
    }
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const canonicalUrl = `https://slicksignature.com${categorySlug ? `/blog/category/${categorySlug}` : '/blog'}${safePage > 1 ? `?page=${safePage}` : ''}`;

  useStructuredData(categorySlug ? `/blog/category/${categorySlug}` : '/blog');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB2F5B] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{categorySlug ? `${categorySlug.replace(/-/g, ' ')} articles` : 'Blog'} | SlickSignature</title>
        <meta
          name="description"
          content="Email signature tips, tutorials and best practices from SlickSignature."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      </Helmet>

      {/* Header */}
      <header className="bg-black text-white py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <Link to="/">
              <img 
                src="https://cdn.slicksignature.com/landing/slicksignature-logo-white.png"
                alt="SlickSignature"
                className="h-12"
              />
            </Link>
            <div className="flex gap-4">
              <Button 
                onClick={() => navigate('/checkout?plan=business')} 
                className="bg-white text-[#EB2F5B] hover:bg-gray-100"
              >
                Start Free Trial
              </Button>
              <Button onClick={() => navigate('/login')} variant="outline" className="text-white border-white">
                Login
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#EB2F5B] to-[#d02850] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-4">
            {selectedCategory && categories.length > 0
              ? `${categories.find(c => c.id === selectedCategory)?.name || 'Blog'}`
              : 'SlickSignature Blog'}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            {selectedCategory && categories.length > 0
              ? categories.find(c => c.id === selectedCategory)?.description || 'Browse articles in this category'
              : 'Tips, tutorials, and insights on professional email signatures and branding'}
          </p>
          {selectedCategory && (
            <div className="mt-4">
              <button
                onClick={() => navigate('/blog')}
                className="text-white/90 hover:text-white underline text-sm"
              >
                ← Back to all posts
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-6">
            {/* Search */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold mb-3">Search</h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search posts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Categories */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold mb-3">Categories</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate('/blog')}
                    className={`block w-full text-left px-3 py-2 rounded ${
                      selectedCategory === null
                        ? 'bg-[#EB2F5B] text-white'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    All Posts ({posts.length})
                  </button>
                  {categories.map((cat) => {
                    const count = posts.filter(p => 
                      p.categories && p.categories.includes(cat.id)
                    ).length;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => navigate(`/blog/category/${cat.slug}`)}
                        className={`block w-full text-left px-3 py-2 rounded ${
                          selectedCategory === cat.id
                            ? 'bg-[#EB2F5B] text-white'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        {cat.name} ({count})
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <Card className="bg-gradient-to-br from-[#EB2F5B] to-[#d02850] text-white">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2">Try SlickSignature</h3>
                <p className="text-white/90 mb-4 text-sm">
                  Create professional email signatures for your team in minutes.
                </p>
                <Button 
                  onClick={() => navigate('/checkout?plan=business')}
                  className="w-full bg-white text-[#EB2F5B] hover:bg-gray-100"
                >
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
          </aside>

          {/* Posts Grid */}
          <div className="lg:col-span-3">
            {filteredPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">No posts found.</p>
                {(selectedCategory || searchQuery) && (
                  <Button 
                    onClick={() => {
                      setSelectedCategory(null);
                      setSearchQuery('');
                    }}
                    className="mt-4"
                    variant="outline"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blog-posts-grid">
                {pagedPosts.map((post) => (
                  <Card key={post.id} className="hover:shadow-lg transition-shadow">
                    <Link to={getPostUrl(post)}>
                      {post.thumbnail?.url && (
                        <img
                          src={getImageUrl(post.thumbnail.url)}
                          alt={post.thumbnail.alt_text || post.title}
                          className="w-full h-48 object-cover"
                        />
                      )}
                      <CardContent className="p-6">
                        {/* Meta */}
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(post.published_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span>{post.author_name}</span>
                          </div>
                        </div>

                        {/* Title */}
                        <h2 className="text-xl font-bold text-gray-900 mb-2 hover:text-[#EB2F5B]">
                          {post.title}
                        </h2>

                        {/* Excerpt */}
                        {post.excerpt && (
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                            {post.excerpt}
                          </p>
                        )}

                        {/* Read More */}
                        <div className="flex items-center text-[#EB2F5B] font-semibold text-sm">
                          Read More <ArrowRight className="h-4 w-4 ml-1" />
                        </div>
                      </CardContent>
                    </Link>
                  </Card>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <nav className="flex items-center justify-between mt-10" data-testid="blog-pagination">
                <Button
                  variant="outline"
                  onClick={() => goToPage(safePage - 1)}
                  disabled={safePage === 1}
                  data-testid="blog-pagination-prev"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      data-testid={`blog-pagination-page-${page}`}
                      className={`h-9 w-9 rounded-md text-sm transition-colors ${
                        page === safePage
                          ? 'bg-[#EB2F5B] text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      aria-current={page === safePage ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <Button
                  variant="outline"
                  onClick={() => goToPage(safePage + 1)}
                  disabled={safePage === totalPages}
                  data-testid="blog-pagination-next"
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </nav>
            )}

            <p className="text-sm text-gray-500 mt-4 text-center" data-testid="blog-posts-count">
              Showing {pagedPosts.length} of {filteredPosts.length} articles
              {totalPages > 1 ? ` · page ${safePage} of ${totalPages}` : ''}
            </p>
          </div>
        </div>
      </div>

      <CTABanner />
      <Footer />
    </div>
  );
};

export default BlogListing;
